import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Users, UserPlus, ShieldCheck, User, KeyRound, Trash2, Loader2, X } from "lucide-react";

const roleBadge = (role) =>
  role === "admin"
    ? "bg-cyan-400/15 text-cyan-300 border-cyan-400/40"
    : "bg-slate-700/50 text-slate-300 border-slate-600";

export default function GestioneUtenti() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [resetFor, setResetFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error("Errore nel caricamento degli utenti");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const changeRole = async (u) => {
    const nuovo = u.role === "admin" ? "operatore" : "admin";
    try {
      await api.patch(`/users/${u.id}/role`, { role: nuovo });
      toast.success(`Ruolo aggiornato: ${u.email} → ${nuovo === "admin" ? "Admin" : "Sales"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel cambio ruolo");
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Eliminare l'utente ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Utente eliminato");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  return (
    <Layout>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 text-cyan-400 text-xs uppercase tracking-widest mb-1">
            <Users className="w-4 h-4" /> Amministrazione
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-100 tracking-tight">Gestione Utenti</h1>
          <p className="text-slate-400 text-sm mt-1">Crea account, reimposta password e gestisci i permessi</p>
        </div>
        <button data-testid="add-user-btn" onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold text-sm self-start sm:self-auto">
          <UserPlus className="w-4 h-4" /> Nuovo Utente
        </button>
      </div>

      <div className="glass rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid="users-table">
            <thead>
              <tr className="border-b border-slate-700/60">
                {["Utente", "Email", "Ruolo", "Creato", "Azioni"].map((h) => (
                  <th key={h} className="text-xs font-semibold uppercase tracking-wider text-slate-400 px-4 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-500">Nessun utente.</td></tr>
              ) : (
                users.map((u) => {
                  const isSelf = me?.email === u.email;
                  return (
                    <tr key={u.id} data-testid={`user-row-${u.email}`} className="border-b border-slate-800/60 hover:bg-slate-700/20 transition-colors">
                      <td className="px-4 py-3 text-slate-200 text-sm font-medium flex items-center gap-2">
                        {u.role === "admin" ? <ShieldCheck className="w-4 h-4 text-cyan-400" /> : <User className="w-4 h-4 text-slate-500" />}
                        {u.name || "—"} {isSelf && <span className="text-[10px] uppercase tracking-wider text-slate-500">(tu)</span>}
                      </td>
                      <td className="px-4 py-3 text-slate-400 text-sm font-mono-tech">{u.email}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-semibold border px-2 py-1 rounded ${roleBadge(u.role)}`}>{u.role === "admin" ? "Admin" : "Sales"}</span>
                      </td>
                      <td className="px-4 py-3 text-slate-500 text-xs">{(u.created_at || "").slice(0, 10) || "—"}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <button data-testid={`role-${u.email}`} onClick={() => changeRole(u)} disabled={isSelf}
                            title={isSelf ? "Non puoi cambiare il tuo ruolo" : "Cambia ruolo"}
                            className="px-2.5 h-8 rounded-md bg-slate-700/40 text-slate-300 hover:bg-slate-600/60 text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed">
                            {u.role === "admin" ? "→ Sales" : "→ Admin"}
                          </button>
                          <button data-testid={`reset-${u.email}`} onClick={() => setResetFor(u)}
                            title="Reimposta password"
                            className="w-8 h-8 rounded-md bg-amber-500/15 text-amber-300 hover:bg-amber-500/25 flex items-center justify-center"><KeyRound className="w-4 h-4" /></button>
                          <button data-testid={`del-user-${u.email}`} onClick={() => removeUser(u)} disabled={isSelf}
                            title={isSelf ? "Non puoi eliminare te stesso" : "Elimina utente"}
                            className="w-8 h-8 rounded-md bg-red-500/15 text-red-300 hover:bg-red-500/25 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showAdd && <AddUserModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {resetFor && <ResetModal target={resetFor} onClose={() => setResetFor(null)} />}
    </Layout>
  );
}

const AddUserModal = ({ onClose, onSaved }) => {
  const [form, setForm] = useState({ email: "", name: "", role: "operatore", password: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.email.trim()) return toast.error("Inserisci un'email");
    if (form.password.length < 8) return toast.error("La password deve avere almeno 8 caratteri");
    setSaving(true);
    try {
      await api.post("/users", { ...form, email: form.email.trim().toLowerCase() });
      toast.success("Utente creato");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="glass rounded-2xl p-6 w-full max-w-md fade-in" onClick={(e) => e.stopPropagation()} data-testid="add-user-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-semibold text-slate-100">Nuovo Utente</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-wider">Email</label>
            <input data-testid="new-user-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
              className="mt-1 w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 focus:outline-none focus:border-cyan-400/60 text-sm" />
          </div>
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-wider">Nome</label>
            <input data-testid="new-user-name" type="text" value={form.name} onChange={(e) => set("name", e.target.value)}
              className="mt-1 w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 focus:outline-none focus:border-cyan-400/60 text-sm" />
          </div>
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-wider">Ruolo</label>
            <select data-testid="new-user-role" value={form.role} onChange={(e) => set("role", e.target.value)}
              className="mt-1 w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 focus:outline-none focus:border-cyan-400/60 text-sm">
              <option value="operatore">Sales (Operatore)</option>
              <option value="admin">Amministratore</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-wider">Password (min 8 caratteri)</label>
            <input data-testid="new-user-password" type="text" value={form.password} onChange={(e) => set("password", e.target.value)}
              className="mt-1 w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 font-mono-tech focus:outline-none focus:border-cyan-400/60 text-sm" />
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-700/40 text-sm">Annulla</button>
          <button data-testid="save-user-btn" onClick={save} disabled={saving}
            className="flex-1 py-2.5 rounded-lg bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold text-sm disabled:opacity-60 flex items-center justify-center gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Crea
          </button>
        </div>
      </div>
    </div>
  );
};

const ResetModal = ({ target, onClose }) => {
  const [pwd, setPwd] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (pwd.length < 8) return toast.error("La password deve avere almeno 8 caratteri");
    setSaving(true);
    try {
      await api.patch(`/users/${target.id}/password`, { password: pwd });
      toast.success(`Password reimpostata per ${target.email}`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel reset password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="glass rounded-2xl p-6 w-full max-w-md fade-in" onClick={(e) => e.stopPropagation()} data-testid="reset-password-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-semibold text-slate-100">Reimposta Password</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-slate-400 mb-3">Utente: <span className="text-slate-200 font-mono-tech">{target.email}</span></p>
        <label className="text-xs text-slate-400 uppercase tracking-wider">Nuova Password (min 8 caratteri)</label>
        <input data-testid="reset-password-input" type="text" value={pwd} onChange={(e) => setPwd(e.target.value)} autoFocus
          className="mt-1 w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 font-mono-tech focus:outline-none focus:border-cyan-400/60 text-sm" />
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-700/40 text-sm">Annulla</button>
          <button data-testid="save-reset-btn" onClick={save} disabled={saving}
            className="flex-1 py-2.5 rounded-lg bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold text-sm disabled:opacity-60 flex items-center justify-center gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Salva
          </button>
        </div>
      </div>
    </div>
  );
};
